import React, { useEffect, useState } from 'react';
import {
  Card, Tabs, Switch, DatePicker, Button, Modal, message, Input,
  Table, Tag, Divider, Alert, Space, Spin
} from 'antd';
import {
  ShopOutlined,
  ClockCircleOutlined,
  NotificationOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useGetLocationByIdQuery,
  useUpdateLocationSettingsMutation,
} from './location.service';
import {
  useFetchSelfRestaurantDataQuery,
  useUpdateRestaurantSettingMutation,
} from '../restaurant/restaruant.service';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const LocationSettings = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = useSelector((state) => state.app.user);

  const locationId = id || user?.activeLocationId || user?.restaurantId;

  const [holidayRange, setHolidayRange] = useState(null);
  const [isHolidayModalVisible, setIsHolidayModalVisible] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [showReopenPrompt, setShowReopenPrompt] = useState(false);
  const [loading, setLoading] = useState(false);

  const { data: locationData, isLoading } = useGetLocationByIdQuery(locationId);
  const { data: restaurantData, refetch: refetchRestaurant } = useFetchSelfRestaurantDataQuery(locationId, {
    refetchOnMountOrArgChange: true,
  });

  const [updateRestaurantSetting] = useUpdateRestaurantSettingMutation();

  const location = locationData?.data;
  const restaurant = restaurantData?.data?.restaurant;
  const isHolidayMode = restaurant?.isHolidayMode || false;

  // ✅ Opening Hours Data
  const getOpeningHoursData = () => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    return days.map((day, index) => {
      const hours = restaurant?.openingHours?.[day] || {};
      return {
        day,
        dayName: dayNames[index],
        isOpen: hours?.isOpen ?? (index < 5),
        openTime: hours?.openTime || '09:00',
        closeTime: hours?.closeTime || '18:00',
      };
    });
  };

  const columns = [
    { title: "Day", dataIndex: "dayName", key: "day", width: 150 },
    {
      title: "Status",
      dataIndex: "isOpen",
      key: "status",
      width: 120,
      render: (isOpen) => (
        <Tag color={isOpen ? "green" : "red"}>
          {isOpen ? "Open ✅" : "Closed ❌"}
        </Tag>
      )
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button size="small" onClick={() => setEditingDay(record)}>
          Change
        </Button>
      )
    }
  ];

  // ✅ Holiday Handlers
  const handleEnableHoliday = async () => {
    if (!holidayRange || !holidayRange[0] || !holidayRange[1]) {
      message.warning("Please select holiday dates first");
      return;
    }
    setLoading(true);
    try {
      await updateRestaurantSetting({
        isHolidayMode: true,
        holidayStart: holidayRange[0].toISOString(),
        holidayEnd: holidayRange[1].toISOString(),
        holidayReason: "Holiday",
      }).unwrap();

      message.success(`Holiday mode enabled`);
      refetchRestaurant();
      setIsHolidayModalVisible(false);
    } catch (error) {
      message.error("Failed to enable holiday mode");
    } finally {
      setLoading(false);
    }
  };

  const handleDisableHoliday = async () => {
    setLoading(true);
    try {
      await updateRestaurantSetting({
        isHolidayMode: false,
        holidayStart: null,
        holidayEnd: null,
        holidayReason: '',
      }).unwrap();

      message.success("Holiday mode disabled");
      setShowReopenPrompt(false);
      refetchRestaurant();
    } catch (error) {
      message.error("Failed to disable holiday mode");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDay = async (day, isOpen) => {
    setLoading(true);
    try {
      const currentHours = restaurant?.openingHours || {};
      const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      
      const updatedHours = {};
      allDays.forEach(d => {
        const existing = currentHours[d] || {};
        if (d === day) {
          updatedHours[d] = {
            isOpen: isOpen,
            openTime: existing.openTime || '09:00',
            closeTime: existing.closeTime || '18:00',
          };
        } else {
          updatedHours[d] = {
            isOpen: existing.isOpen ?? (d !== 'saturday' && d !== 'sunday'),
            openTime: existing.openTime || '09:00',
            closeTime: existing.closeTime || '18:00',
          };
        }
      });

      await updateRestaurantSetting({ openingHours: updatedHours }).unwrap();
      message.success("Opening hours updated");
      setEditingDay(null);
      refetchRestaurant();
    } catch (error) {
      message.error("Failed to update");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Check holiday ended
  useEffect(() => {
    if (restaurant?.isHolidayMode && restaurant?.holidayEnd) {
      const now = new Date();
      const holidayEnd = new Date(restaurant.holidayEnd);
      setShowReopenPrompt(now <= holidayEnd);
    }
  }, [restaurant]);

  const openingHoursData = getOpeningHoursData();

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card
      bordered={false}
      title={
        <Space>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/locations')}
          />
          <span>Settings - {location?.name || 'Location'}</span>
        </Space>
      }
    >
      <Tabs defaultActiveKey="schedule">
        <TabPane tab={<span><ShopOutlined /> Schedule</span>} key="schedule">
          <div>
            <Alert message="Opening Hours" type="info" showIcon style={{ marginBottom: 16 }} />
            <Table dataSource={openingHoursData} columns={columns} pagination={false} rowKey="day" size="small" />

            <Divider />

            <Alert message="Holiday Mode" type="warning" showIcon style={{ marginBottom: 16 }} />

            {isHolidayMode && (
              <Alert
                message="Holiday Mode Active"
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                action={<Button size="small" danger onClick={handleDisableHoliday} loading={loading}>Reopen Now</Button>}
              />
            )}

            <RangePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              value={holidayRange}
              onChange={setHolidayRange}
              disabled={isHolidayMode}
              disabledDate={(current) => current && current < dayjs().startOf("day")}
              placeholder={["Start Date", "End Date"]}
            />

            <Button
              type="primary"
              onClick={() => setIsHolidayModalVisible(true)}
              disabled={isHolidayMode || !holidayRange || !holidayRange[0] || !holidayRange[1]}
              block
              loading={loading}
              style={{ marginTop: 16 }}
            >
              Start Holiday Mode
            </Button>
          </div>
        </TabPane>

        <TabPane tab={<span><NotificationOutlined /> Alerts</span>} key="alerts">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <strong>EMAIL:</strong>
              <Input placeholder="alert@example.com" value={restaurant?.alertEmail || ''} style={{ maxWidth: 300 }} />
              <Button type="primary">UPDATE</Button>
            </div>
          </div>
        </TabPane>
      </Tabs>

      {/* Modals */}
      <Modal title="Enable Holiday Mode" open={isHolidayModalVisible} onOk={handleEnableHoliday} onCancel={() => setIsHolidayModalVisible(false)} confirmLoading={loading}>
        <p>Enable holiday mode? Kitchen will be marked as closed.</p>
      </Modal>

      <Modal title={`Change ${editingDay?.dayName} Status`} open={!!editingDay} onCancel={() => setEditingDay(null)} footer={null}>
        {editingDay && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Switch
              checked={editingDay.isOpen}
              onChange={(checked) => setEditingDay({ ...editingDay, isOpen: checked })}
              checkedChildren="Open ✅"
              unCheckedChildren="Closed ❌"
              size="large"
            />
            <div style={{ marginTop: 20, textAlign: "right" }}>
              <Button onClick={() => setEditingDay(null)}>Cancel</Button>
              <Button type="primary" onClick={() => handleUpdateDay(editingDay.day, editingDay.isOpen)} loading={loading}>Save</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal title="🔄 Kitchen Reopening" open={showReopenPrompt} closable={false} footer={[
        <Button key="remind" onClick={() => setShowReopenPrompt(false)}>Remind Later</Button>,
        <Button key="reopen" type="primary" onClick={handleDisableHoliday} loading={loading}>Yes, Reopen Kitchen</Button>
      ]}>
        <div style={{ textAlign: "center", padding: "20px" }}>
          <ClockCircleOutlined style={{ fontSize: 48, color: "#faad14" }} />
          <h3>Holiday Period Has Ended</h3>
          <p>Do you want to reopen your kitchen?</p>
        </div>
      </Modal>
    </Card>
  );
};

export default LocationSettings;